import { useState, useRef } from 'react';

interface ImageUploaderProps {
  images: string[];
  onChange: (images: string[]) => void;
  max?: number;
  label?: string;
}

async function uploadToStorage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = (reader.result as string).split(',')[1];
        const supabaseUrl = (import.meta as Record<string, unknown>).env
          ? (import.meta as { env: Record<string, string> }).env.VITE_PUBLIC_SUPABASE_URL
          : 'https://fdxjebyoqhgkrbiofazf.supabase.co';

        const res = await fetch(`${supabaseUrl}/functions/v1/product-upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fileName: file.name,
            fileData: base64Data,
            contentType: file.type,
          }),
        });

        const data = await res.json();
        if (data.publicUrl) {
          resolve(data.publicUrl);
        } else {
          reject(new Error(data.error || 'Upload failed'));
        }
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

export default function ImageUploader({ images, onChange, max = 8, label }: ImageUploaderProps) {
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const remaining = max - images.length;
    const toUpload = Array.from(files).slice(0, remaining);

    for (const file of toUpload) {
      if (!file.type.startsWith('image/')) continue;
      if (file.size > 10 * 1024 * 1024) {
        alert(`图片 ${file.name} 超过 10MB 限制`);
        continue;
      }

      setUploading(file.name);
      try {
        const url = await uploadToStorage(file);
        onChange([...images, url]);
      } catch {
        alert(`上传 ${file.name} 失败，请重试`);
      }
    }
    setUploading(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemove = (index: number) => {
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const dt = new DataTransfer();
      const remaining = max - images.length;
      Array.from(files).slice(0, remaining).forEach((f) => dt.items.add(f));
      if (fileInputRef.current) {
        fileInputRef.current.files = dt.files;
        fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {label && <label className="text-sm font-medium text-[#333]">{label}</label>}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="grid grid-cols-3 lg:grid-cols-4 gap-3"
      >
        {images.map((url, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-[#ede9e3] bg-[#f7f5f2] group">
            <img src={url} alt={`图片${i + 1}`} className="w-full h-full object-cover object-top" />
            <button
              type="button"
              onClick={() => handleRemove(i)}
              className="absolute top-1.5 right-1.5 w-6 h-6 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-red-500 transition-colors cursor-pointer opacity-0 group-hover:opacity-100"
            >
              <i className="ri-close-line text-xs"></i>
            </button>
          </div>
        ))}

        {images.length < max && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading !== null}
            className="aspect-square rounded-xl border-2 border-dashed border-[#ddd] bg-[#faf9f7] hover:border-[#C8714A] hover:bg-[#C8714A]/5 transition-colors cursor-pointer flex flex-col items-center justify-center gap-1.5 group"
          >
            {uploading ? (
              <>
                <div className="w-6 h-6 border-2 border-[#C8714A] border-t-transparent rounded-full animate-spin"></div>
                <span className="text-xs text-[#aaa]">上传中...</span>
              </>
            ) : (
              <>
                <div className="w-8 h-8 flex items-center justify-center text-[#ccc] group-hover:text-[#C8714A] transition-colors">
                  <i className="ri-add-line text-xl"></i>
                </div>
                <span className="text-xs text-[#aaa] group-hover:text-[#C8714A] transition-colors">
                  {images.length === 0 ? '上传图片' : '添加图片'}
                </span>
              </>
            )}
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}